import { useGetSubscription, useListCreditUsagePerUser } from "@/api/stripe";
import { useUserInfo } from "@/api/user";
import {
  EmptyState,
  LcBadge,
  PaneBody,
  PaneSection,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { FreeTrialBanner } from "@/components/billing/FreeTrialBanner";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { useEnterprisePlan } from "@/hooks/useEnterprisePlan";
import { useIsSelfServeBilling } from "@/hooks/useIsSelfServeBilling";
import { trackBillingPlanChangeClicked } from "@/utils/analytics";
import { formatCurrencyFromCents, formatInteger } from "@/utils/currency";
import { formatTrialDaysRemaining, trialDaysRemaining } from "@/utils/trial";
import type { UsageRecordSumResult } from "@cohesive/api-client";
import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Usage · Overview — what the plan is and what it has been spent on this
 * period. Money mechanics (payment method, invoices, cancellation) live one
 * scope over in Billing.
 */

function planStatus(
  status: string | undefined,
  cancelAt: string | null | undefined,
): { label: string; tone: "active" | "warning" | "blocked" } | null {
  if (!status) return null;
  if (status === "trialing") return { label: "Free trial", tone: "warning" };
  if (status === "canceled") return { label: "Cancelled", tone: "blocked" };
  if (cancelAt) {
    return {
      label: `Ends ${new Date(cancelAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`,
      tone: "warning",
    };
  }
  if (status === "past_due" || status === "unpaid") {
    return { label: "Past due", tone: "blocked" };
  }
  return { label: "Active", tone: "active" };
}

export const UsageOverviewPane: React.FC = () => {
  const { data: userInfo } = useUserInfo();
  const navigate = useNavigate();
  const { data: subscription, isLoading: isSubscriptionLoading } =
    useGetSubscription();
  const { data: creditUsage, isLoading: isCreditUsageLoading } =
    useListCreditUsagePerUser();
  const isEnterprisePlan = useEnterprisePlan();
  const isSelfServeBilling = useIsSelfServeBilling();

  const plan = subscription?.data.stripe_subscription;
  const seatCount = plan?.seat_count ?? 1;
  const includedCreditsPerSeat = plan?.included_credits_per_seat ?? 0;
  const status = planStatus(plan?.status, plan?.cancel_at);
  const results = creditUsage?.data.results ?? [];
  const totalUsed = creditUsage?.data.total_count ?? 0;
  const includedTotal = includedCreditsPerSeat * seatCount;
  const usedPct =
    includedTotal > 0
      ? Math.min(100, Math.round((totalUsed / includedTotal) * 100))
      : 0;
  // The one lifecycle vocabulary the rail meter uses: warning at 75, blocked at 90.
  const meterColor =
    usedPct >= 90
      ? "var(--lc-blocked)"
      : usedPct >= 75
        ? "var(--lc-warning)"
        : "var(--lc-active)";

  // On-demand: money spent past the included allowance, against the budget
  // set in Billing. -1 is the "unlimited" sentinel, 0 means none allowed.
  const onDemandSpent = creditUsage?.data.total_billable_amount || 0;
  const rawOnDemandCap = plan?.max_credit_overage_spend_per_period ?? 0;
  const isUnlimitedOnDemand = rawOnDemandCap === -1;
  const onDemandCap = isUnlimitedOnDemand ? 0 : rawOnDemandCap;
  const onDemandPct =
    onDemandCap > 0
      ? Math.min(100, Math.round((onDemandSpent / onDemandCap) * 100))
      : 0;
  const onDemandColor =
    onDemandPct >= 90
      ? "var(--lc-blocked)"
      : onDemandPct >= 75
        ? "var(--lc-warning)"
        : "var(--lc-active)";
  const isOnFreeTrial = plan?.status === "trialing";

  const trialDaysLabel = formatTrialDaysRemaining(
    trialDaysRemaining(plan?.current_period_end),
  );

  const renewsOn = plan?.current_period_end
    ? new Date(plan.current_period_end).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <PaneBody>
      <div className="flex items-start justify-between gap-3 px-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3
              data-testid="current-plan-name"
              className="text-md font-medium text-n-90"
            >
              {plan?.plan_display_name || "Custom"} plan
            </h3>
            {status && <LcBadge tone={status.tone}>{status.label}</LcBadge>}
          </div>
          {isSubscriptionLoading ? (
            <div className="mt-1 h-4 w-40 animate-pulse rounded bg-n-10 dark:bg-n-20" />
          ) : plan ? (
            <div className="mt-0.5 space-y-0.5 text-sm text-n-50">
              {!isEnterprisePlan && (
                <p data-testid="seat-count">
                  {seatCount} seat{seatCount === 1 ? "" : "s"}
                  {plan.seat_price
                    ? ` · ${formatCurrencyFromCents(plan.seat_price)} per seat / ${
                        plan.billing_interval === "year" ? "year" : "month"
                      }`
                    : ""}
                </p>
              )}
              {/* Two lines, as the design system's plan masthead runs. The
                  per-seat credit allowance was a third: the meter below
                  already states the allowance in the only form that matters
                  — how much of it is left. */}
              {renewsOn &&
                (isOnFreeTrial ? (
                  <p>
                    Trial ends {renewsOn}
                    {trialDaysLabel ? ` · ${trialDaysLabel}` : ""}
                  </p>
                ) : (
                  <p>Renews {renewsOn}</p>
                ))}
            </div>
          ) : (
            <p className="mt-0.5 text-sm text-n-50">
              Free plan — no subscription
            </p>
          )}
        </div>
        {isSelfServeBilling && (
          <Button
            data-testid="change-plan-button"
            variant="secondary"
            size="sm"
            className={smButtonClassName}
            disabled={isSubscriptionLoading}
            onClick={() => {
              trackBillingPlanChangeClicked({
                currentPlan: plan?.plan_name,
                source: "usage_billing",
                organization_id: userInfo?.organization?.id,
              });
              navigate(ROUTES.BILLING.PLANS);
            }}
          >
            Change plan
          </Button>
        )}
      </div>

      {/* Below the plan it describes, not above it. Leading with the banner
          pushed the masthead a banner's height down the pane, so Overview
          started lower than every other scope in the modal. Its status badge
          overhangs its box by 12px, and the pane column clips, so it keeps
          that much gutter to sit in. */}
      <div className="ml-4 mr-2.5">
        <FreeTrialBanner subscription={subscription} />
      </div>

      <PaneSection head="This cycle">
        <div className="px-2.5 pt-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-md font-medium text-n-90">Credits</span>
            {isCreditUsageLoading ? (
              <span className="h-4 w-24 animate-pulse rounded bg-n-10 dark:bg-n-20" />
            ) : (
              <span className="font-mono text-xs text-n-50">
                {includedTotal > 0
                  ? `${formatInteger(totalUsed)} / ${formatInteger(includedTotal)}`
                  : formatInteger(totalUsed)}
              </span>
            )}
          </div>
          {includedTotal > 0 && (
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-n-10 dark:bg-n-20">
              <div
                className="h-full rounded-full"
                style={{ width: `${usedPct}%`, background: meterColor }}
              />
            </div>
          )}
          <p className="mt-2 text-sm text-n-50">
            {renewsOn ? `Resets ${renewsOn}.` : "Resets each cycle."}
          </p>
        </div>

        {/* On-demand gets the meter treatment too, not a clause at the end of
            the credits sentence. It's money rather than credits, and it has
            its own ceiling — the budget from Billing — so spend-against-cap is
            the same question the credits bar answers. Trials can't accrue it. */}
        {!isOnFreeTrial && (
          <div className="px-2.5 pt-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-md font-medium text-n-90">On-demand</span>
              {isCreditUsageLoading ? (
                <span className="h-4 w-24 animate-pulse rounded bg-n-10 dark:bg-n-20" />
              ) : (
                <span className="font-mono text-xs text-n-50">
                  {formatCurrencyFromCents(onDemandSpent)}
                  {onDemandCap > 0
                    ? ` / ${formatCurrencyFromCents(onDemandCap)}`
                    : ""}
                </span>
              )}
            </div>
            {onDemandCap > 0 && (
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-n-10 dark:bg-n-20">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${onDemandPct}%`,
                    background: onDemandColor,
                  }}
                />
              </div>
            )}
            <p className="mt-2 text-sm text-n-50">
              {isUnlimitedOnDemand
                ? "Charged beyond your included credits. No budget cap set."
                : onDemandCap > 0
                  ? "Charged beyond your included credits. Agents pause at the cap."
                  : "On-demand spending is off — agents pause once credits run out."}
            </p>
          </div>
        )}

        {results.length > 0 && (
          <div className="mt-3 flex flex-col gap-1.5 px-2.5">
            {results.map((user: UsageRecordSumResult) => (
              <div key={user.user_id} className="flex items-center gap-3">
                <span className="w-28 shrink-0 truncate text-sm text-n-70">
                  {user.user_name || user.user_email || "Unknown"}
                </span>
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-n-10 dark:bg-n-20">
                  <div
                    className="h-full rounded-full bg-n-90"
                    style={{
                      width: `${Math.min(
                        100,
                        Math.round((user.count / Math.max(totalUsed, 1)) * 100),
                      )}%`,
                    }}
                  />
                </div>
                <span className="w-14 shrink-0 text-right font-mono text-2xs text-n-40">
                  {formatInteger(user.count)}
                </span>
              </div>
            ))}
          </div>
        )}

        {!isCreditUsageLoading && results.length === 0 && (
          <EmptyState
            title="No usage yet."
            description="Credit usage appears here once your team starts working."
          />
        )}
      </PaneSection>
    </PaneBody>
  );
};
